/** Human-friendly recipient labels — mirrors src/domain/recipient_display.py */

export function isNumericChatId(chatId: string | null | undefined): boolean {
  return Boolean(chatId && /^-?\d+$/.test(String(chatId).trim()));
}

export type RecipientLabelInput = {
  display_name?: string | null;
  telegram_username?: string | null;
  chat_id?: string | null;
  telegram_resolution_method?: string | null;
  telegram_resolved_chat_id?: string | null;
  delivery_provider?: 'auto' | 'bot' | 'telethon' | null;
  source?: 'known_chat' | 'manual' | 'bot' | 'telethon' | null;
};

export function recipientPrimaryLabel(r: RecipientLabelInput): string {
  const { display_name, telegram_username, chat_id } = r;
  if (display_name?.trim()) return display_name.trim();
  if (telegram_username?.trim()) return `@${telegram_username.trim().replace(/^@/, '')}`;
  if (chat_id && !isNumericChatId(chat_id)) return chat_id.trim();
  if (chat_id) return chat_id.trim();
  return '—';
}

export function recipientSecondaryLabel(r: RecipientLabelInput): string | null {
  const { display_name, telegram_username, chat_id, telegram_resolution_method } = r;
  if (telegram_username?.trim() && chat_id && isNumericChatId(chat_id)) {
    const handle = `@${telegram_username.trim().replace(/^@/, '')}`;
    return `${handle} · ${chat_id.trim()}`;
  }
  if (chat_id === 'me') return 'Telethon · me';
  const method = telegram_resolution_method || '';
  if (method.startsWith('telethon_') && chat_id) return `Telethon · ${chat_id}`;
  if ((method.startsWith('bot_') || method === 'numeric') && chat_id) return `BOT · ${chat_id}`;
  if (chat_id && isNumericChatId(chat_id) && display_name?.trim()) return chat_id.trim();
  return null;
}

export function recipientTooltip(r: RecipientLabelInput): string {
  const parts = [r.chat_id, r.telegram_resolved_chat_id].filter(Boolean);
  return parts.join(' · ') || recipientPrimaryLabel(r);
}

export function deliveryRouteLabel(r: RecipientLabelInput): string {
  const routeId = r.telegram_resolved_chat_id || r.chat_id || '—';
  const provider = r.delivery_provider || 'auto';
  const method = r.telegram_resolution_method || '';

  if (provider === 'telethon' || (r.source === 'known_chat' && provider !== 'bot')) {
    return `TELETHON · ${routeId}`;
  }
  if (provider === 'bot') {
    return `BOT · ${routeId}`;
  }
  if (r.chat_id === 'me' || method.startsWith('telethon_')) {
    return `TELETHON · ${routeId}`;
  }
  if (method.startsWith('bot_') || method === 'numeric') {
    return `BOT · ${routeId}`;
  }
  if (isNumericChatId(r.chat_id)) {
    return `AUTO · ${r.chat_id}`;
  }
  if (r.chat_id === 'me') return 'TELETHON · me';
  return `AUTO · ${routeId}`;
}

/** Map subscription API row to RecipientCell input. */
export function subscriptionRecipientInput(sub: {
  recipient_display_name?: string | null;
  recipient_telegram_username?: string | null;
  recipient_chat_id?: string | null;
  user_telegram_chat_id?: string | null;
  recipient_resolution_method?: string | null;
  recipient_resolved_chat_id?: string | null;
}): RecipientLabelInput {
  return {
    display_name: sub.recipient_display_name,
    telegram_username: sub.recipient_telegram_username,
    chat_id: sub.recipient_chat_id || sub.user_telegram_chat_id,
    telegram_resolution_method: sub.recipient_resolution_method,
    telegram_resolved_chat_id: sub.recipient_resolved_chat_id,
  };
}

export function subscriptionRecipientLabels(sub: Parameters<typeof subscriptionRecipientInput>[0]): {
  primary: string;
  secondary: string | null;
  tooltip: string;
} {
  const input = subscriptionRecipientInput(sub);
  return {
    primary: recipientPrimaryLabel(input),
    secondary: recipientSecondaryLabel(input),
    tooltip: recipientTooltip(input),
  };
}

/** Flatten recipient fields for DataTable search/filter. */
export function recipientRowSearchValue(r: RecipientLabelInput & {
  channel?: string | null;
  is_enabled?: boolean;
  subscription_count?: number;
}): string {
  return [
    recipientPrimaryLabel(r),
    recipientSecondaryLabel(r),
    r.chat_id,
    r.telegram_username,
    r.telegram_resolved_chat_id,
    r.channel,
    r.is_enabled ? 'enabled' : 'disabled',
    r.subscription_count,
  ]
    .filter((v) => v != null && v !== '')
    .join(' ');
}
